/**
 * Debug System Type Definitions
 *
 * Core types for the extensible debug capture system.
 * Supports field registration, capture sessions, and export formats.
 */

import type { RevenueDistributionHelperMap } from '~/engine/processing/engines/distribution-engine/types';
import type { BondAllocation } from '~/engine/processing/engines/revenue-engine/distribution-types';
import type {
  BondSeries,
  Candidate,
  CashFlow,
  Configuration,
  DateString,
  RevenueMap,
} from '~/types';

/**
 * Categories for organizing debug fields in UI and exports
 */
export type DebugFieldCategory =
  | 'payment' // Payment summary fields (active bonds, totals)
  | 'revenue' // Revenue distribution fields
  | 'interest' // Interest calculation fields
  | 'principal' // Principal distribution fields
  | 'drf' // Debt Retirement Fund fields
  | 'tax' // Tax-related fields (Winter Tax, Summer Tax)
  | 'compliance' // Compliance checks (5-year rule, etc.)
  | 'timing' // Payment timing and dates
  | 'bond' // Per-bond breakdown fields
  | 'metadata'; // Processing metadata

/**
 * Decision context captured during principal calculation
 * These are the ACTUAL values the calculator had when making decisions
 *
 * IMPORTANT: These values are captured AT decision time (before cascading).
 * They may differ from final cashflow values due to interest cascading.
 */
export interface PrincipalDecisionContext {
  // === INPUTS (what the calculator saw) ===

  /** Current record's revenue minus existing debt */
  currentRevenueMinusDebt: number;

  /** Current record's interest obligation AT decision time */
  currentInterestObligation: number;

  /** Next record's revenue minus existing debt (null if no next) */
  nextRevenueMinusDebt: number | null;

  /** Next record's interest obligation AT decision time (CRITICAL - may be $0 pre-cascade) */
  nextInterestObligation: number | null;

  /** Whether next record is interest-only */
  nextIsInterestOnly: boolean;

  /** Maximum interest rate among active bonds */
  maxInterestRate: number;

  // === COMPUTED VALUES ===

  /** Available for bonds from current record: revenueMinusDebt - interestObligation */
  currentAvailableForBonds: number;

  /** Available for bonds from next record (if applicable) */
  nextAvailableForBonds: number | null;

  /** Total combined revenue for distribution (current + next if I/O) */
  totalRevenueForDistribution: number;

  /** Interest divisor: (1 + rate) if next is I/O, else (1 + rate/2) */
  interestDivisor: number;

  /** Raw principal before rounding: totalRevenue / divisor */
  rawPrincipalCapacity: number;

  // === RESULT ===

  /** Final rounded principal available for distribution */
  roundedPrincipalAvailable: number;

  // === MIN CONSTRAINT DEBUG INFO ===

  /** The min constraint value from calculateMinConstraintPure (Infinity if not applicable) */
  minConstraint: number | null;

  /** The minimum effectiveAvailable found across earlier payments */
  minAvailable: number | null;

  /** Payment index that had the minimum effectiveAvailable */
  minAvailablePaymentIndex: number | null;

  /** Payment date that had the minimum effectiveAvailable (for readability) */
  minAvailablePaymentDate: string | null;

  /** DRF starting balance used in constraint calculation (static configuration value) */
  drfStartingBalanceUsed: number | null;

  /** Projected DRF balance at the constraining payment (dynamic, accounts for allocations) */
  projectedDrfAtConstraint: number | null;

  /** Whether the min constraint was the limiting factor (minConstraint < rawPrincipal) */
  wasConstrainedByMinPayment: boolean;

  // === METADATA ===

  /** Current payment date for reference */
  paymentDate: string;

  /** Next payment date for reference (if exists) */
  nextPaymentDate: string | null;

  /** Whether current is interest-only (principal = 0) */
  isInterestOnly: boolean;
}

/**
 * Context provided to field extractors during capture
 * Contains all available data at the capture point
 */
export interface DebugExtractionContext {
  // Core entities
  candidate: Candidate;
  configuration: Configuration;

  // Current payment being processed
  cashFlow: CashFlow;
  paymentIndex: number;

  // Distribution state (available during distribution)
  helperMap?: RevenueDistributionHelperMap;

  /** Calculated bond allocations for this payment (principal, interest, etc.) */
  bondAllocations?: readonly BondAllocation[];

  // Additional context
  revenueMap: RevenueMap;
  bondSeries: BondSeries[];

  /** Decision context captured during principal calculation */
  decisionContext?: PrincipalDecisionContext;
}

/**
 * Debug field definition - describes a capturable value
 * Use this interface to register new debug fields
 */
export interface DebugFieldDefinition<T = unknown> {
  /** Unique identifier (e.g., 'revenue.available', 'interest.total') */
  id: string;

  /** Human-readable name for UI display */
  name: string;

  /** Category for grouping in UI */
  category: DebugFieldCategory;

  /** Description for documentation/tooltips */
  description: string;

  /** Function to extract value from capture context */
  extractor: (ctx: DebugExtractionContext) => T | undefined;

  /** Optional formatter for display (default: toString) */
  formatter?: (value: T) => string;

  /** Optional formula generator for calculation breakdown */
  formula?: (ctx: DebugExtractionContext) => string | undefined;

  /** Performance cost indicator for selective capture */
  performanceCost: 'low' | 'medium' | 'high';

  /** Whether field is captured by default */
  enabledByDefault: boolean;

  /** Schema version when field was added (for compatibility) */
  addedInVersion?: string;
}

/**
 * Captured field value with formatting
 */
export interface CapturedFieldValue {
  /** Raw value */
  value: unknown;

  /** Formatted string for display */
  formatted: string;

  /** Formula breakdown (if available) */
  formula?: string;

  /** Field definition ID */
  fieldId: string;

  /** Field name */
  fieldName: string;
}

/**
 * Snapshot of a single payment's debug state
 */
export interface PaymentSnapshot {
  /** Payment date */
  paymentDate: DateString;

  /** Related tax bill year */
  taxYear: number;

  /** Index in cash flows array */
  paymentIndex: number;

  /** Whether this is an interest-only payment */
  isInterestOnly: boolean;

  /** Is this payment active */
  isActive: boolean;

  /** Captured field values keyed by field ID */
  fields: Record<string, CapturedFieldValue>;

  /** Per-bond breakdown (if captured) */
  bondBreakdown?: BondPaymentSnapshot[];
}

/**
 * Per-bond payment snapshot
 */
export interface BondPaymentSnapshot {
  bondSeriesId: string;
  bondSeriesName: string;
  isActive: boolean;
  fields: Record<string, CapturedFieldValue>;
}

/**
 * Summary statistics for a debug session
 */
export interface DebugSessionSummary {
  totalRevenue: number;
  totalPrincipal: number;
  totalInterest: number;
  endingDRF: number;
  isViable: boolean;
  failureMessages: string[];
  paymentsProcessed: number;
  activeBondCount: number;
}

/**
 * Complete debug session capture
 */
export interface DebugSessionCapture {
  /** Unique session identifier */
  sessionId: string;

  /** Schema version for compatibility */
  schemaVersion: string;

  /** Timestamp when capture started */
  timestamp: number;

  /** Processing duration in milliseconds */
  processingTimeMs: number;

  // Entity identifiers
  candidateId: string;
  candidateName: string;
  configurationId: string;
  projectId?: string;
  millageShapeName?: string;

  // Capture configuration
  capturePreset: DebugCapturePreset;
  enabledFieldIds: string[];

  /** Captured payment snapshots (in processing order - backwards) */
  paymentSnapshots: PaymentSnapshot[];

  /** Summary statistics */
  summary: DebugSessionSummary;

  /** Optional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Debug capture presets
 */
export type DebugCapturePreset = 'minimal' | 'standard' | 'full' | 'custom';

/**
 * Debug capture configuration
 */
export interface DebugCaptureConfig {
  /** Capture preset */
  preset: DebugCapturePreset;

  /** Explicitly enabled field IDs (overrides preset for custom) */
  enabledFields?: string[];

  /** Explicitly disabled field IDs (overrides preset) */
  disabledFields?: string[];

  /** Maximum payments to capture (memory protection) */
  maxPayments: number;

  /** Whether to capture per-bond breakdown */
  captureBondBreakdown: boolean;

  /** Whether to capture formula strings */
  captureFormulas: boolean;
}

/**
 * Export format types
 */
export type DebugExportFormat = 'json' | 'csv' | 'clipboard';

/**
 * JSON export schema (for CLI/agent access)
 */
export interface DebugExportJSON {
  $schema: 'bond-calculator-debug-v1';
  exportedAt: string;
  schemaVersion: string;

  metadata: {
    candidateId: string;
    candidateName: string;
    configurationId: string;
    millageShape: string;
    processingTimeMs: number;
  };

  summary: DebugSessionSummary;

  payments: Array<{
    paymentDate: string;
    taxYear: number;
    paymentIndex: number;
    isInterestOnly: boolean;
    fields: Record<
      string,
      {
        value: unknown;
        formatted: string;
        formula?: string;
      }
    >;
    bondBreakdown?: Array<{
      bondSeriesId: string;
      bondSeriesName: string;
      fields: Record<string, { value: unknown; formatted: string }>;
    }>;
  }>;
}

/**
 * Default capture configuration
 */
export const DEFAULT_CAPTURE_CONFIG: DebugCaptureConfig = {
  preset: 'standard',
  maxPayments: 100,
  captureBondBreakdown: true,
  captureFormulas: true,
};

/**
 * Current schema version
 */
export const DEBUG_SCHEMA_VERSION = '1.0.0';
